<template>
  <div class="flex items-center flex-col">
    <div class="w-full">
      <empty-state v-if="showEmptyResult" :title="emptyMessage" />
      <div v-else class="w-full">
        <section
          v-if="isWhatsappType"
          class="campaigns-table-wrap bg-white dark:bg-slate-900 flex-1 h-full overflow-hidden"
        >
          <ve-table
            :fixed-header="true"
            max-height="calc(100vh - 7.125rem)"
            :columns="columns"
            :table-data="tableData"
            :border-around="false"
            :sort-option="sortOption"
          />
        </section>
        <campaign-card
          v-for="campaign in campaigns"
          v-else
          :key="campaign.id"
          :campaign="campaign"
          :is-ongoing-type="isOngoingType"
          @edit="campaign => $emit('edit', campaign)"
          @cancel="campaign => $emit('cancel', campaign)"
        />
      </div>
    </div>
    <div v-if="isLoading" class="items-center flex text-base justify-center">
      <spinner color-scheme="primary" />
      <span>{{ $t('CAMPAIGN.LIST.LOADING_MESSAGE') }}</span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import { VeTable } from 'vue-easytable';

import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import campaignMixin from 'shared/mixins/campaignMixin';
import CampaignCard from './CampaignCard.vue';
import { CAMPAIGN_STATUSES } from './constants';
import timeMixin from 'dashboard/mixins/time';
import rtlMixin from 'shared/mixins/rtlMixin';

export default {
  components: {
    EmptyState,
    Spinner,
    CampaignCard,
    VeTable,
  },

  mixins: [clickaway, campaignMixin, timeMixin, rtlMixin],
  props: {
    campaigns: {
      type: Array,
      default: () => [],
    },
    showEmptyResult: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    sortParam: {
      type: String,
      default: 'created_at',
    },
    sortOrder: {
      type: String,
      default: 'desc',
    },
  },
  data() {
    return {
      sortConfig: {},
      sortOption: {
        sortAlways: true,
        sortChange: params => this.$emit('on-sort-change', params),
      },
    };
  },
  computed: {
    ...mapGetters({
      audienceList: 'labels/getLabels',
    }),
    currentInboxId() {
      return this.$route.params.inboxId;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.currentInboxId);
    },
    inboxes() {
      if (this.isOngoingType) {
        return this.$store.getters['inboxes/getWebsiteInboxes'];
      }
      return this.$store.getters['inboxes/getTwilioInboxes'];
    },
    emptyMessage() {
      if (this.isOngoingType) {
        return this.inboxes.length
          ? this.$t('CAMPAIGN.ONGOING.404')
          : this.$t('CAMPAIGN.ONGOING.INBOXES_NOT_FOUND');
      }

      return this.inboxes.length
        ? this.$t('CAMPAIGN.ONE_OFF.404')
        : this.$t('CAMPAIGN.ONE_OFF.INBOXES_NOT_FOUND');
    },
    tableData() {
      if (this.isLoading) {
        return [];
      }
      return this.campaigns.map(item => {
        const { created_at: createdAt } = item;
        const audiences = item.audience || [];
        return {
          ...item,
          created_at: createdAt ? this.dynamicTime(createdAt) : '---',
          deliveredTagetCount: '---',
          template_name: item.template_name ? item.template_name : '---',
          creator: item.creator || '---',
          audiences: audiences.map(au => {
            const audience = this.audienceList.find((item) => item.id === au.id);

            if (audience === undefined) {
              return;
            }

            return audience.title;
          }),
        };
      });
    },
    columns() {
      return [
        {
          field: 'title',
          key: 'title',
          title: this.$t('CAMPAIGN.LIST.TABLE_HEADER.CAMPAIGN_NAME'),
          align: this.isRTLView ? 'right' : 'left',
          sortBy: this.sortConfig.title || '',
          width: "40%",
        },
        {
          field: 'message',
          key: 'message',
          title: this.$t('CAMPAIGN.LIST.TABLE_HEADER.MESSAGE'),
          align: this.isRTLView ? 'right' : 'left',
          sortBy: this.sortConfig.message || '',
          width: "60%",
        },
        {
          field: 'created_at',
          key: 'created_at',
          title: this.$t('CAMPAIGN.LIST.TABLE_HEADER.CREATED_AT'),
          align: this.isRTLView ? 'right' : 'left',
          sortBy: this.sortConfig.created_at || '',
          width: "150px",
        },
        {
          field: 'campaign_status',
          key: 'campaign_status',
          title: this.$t('CAMPAIGN.LIST.TABLE_HEADER.STATUS'),
          align: this.isRTLView ? 'right' : 'left',
          width: "100px",
        },
        {
          field: 'audience',
          key: 'audience',
          title: this.$t('CAMPAIGN.LIST.TABLE_HEADER.AUDIENCE_LIST'),
          align: this.isRTLView ? 'right' : 'left',
          width: "150px",

          renderBodyCell: ({ row }) => {
            const { audiences } = row;

            if (!audiences.length) return '---';
            return audiences.map(audience => <div>{audience}</div>);
          },
        },
        {
          field: 'delivered',
          key: 'delivered',
          title: this.$t('CAMPAIGN.LIST.TABLE_HEADER.DELIVERED'),
          align: this.isRTLView ? 'right' : 'left',
          width: "100px",
        },
        {
          field: 'read',
          key: 'read',
          title: this.$t('CAMPAIGN.LIST.TABLE_HEADER.READ'),
          align: this.isRTLView ? 'right' : 'left',
          width: "100px",
        },
        {
          field: 'failed',
          key: 'failed',
          title: this.$t('CAMPAIGN.LIST.TABLE_HEADER.FAILED'),
          align: this.isRTLView ? 'right' : 'left',
          width: "100px",
        },
        {
          field: 'creator',
          key: 'creator',
          title: this.$t('CAMPAIGN.LIST.TABLE_HEADER.CREATOR'),
          align: this.isRTLView ? 'right' : 'left',
          width: "100px",
        },
        {
          field: 'action',
          key: 'action',
          title: '',
          align: this.isRTLView ? 'right' : 'left',
          width: "150px",
          renderBodyCell: ({ row }) => {
            return (
              <div class="flex flex-row space-x-4">
                {this.isOngoingType && (
                  <woot-button
                    variant="link"
                    icon="edit"
                    color-scheme="secondary"
                    size="small"
                    onClick={() => this.editCampaign(row)}
                  >
                    {this.$t('CAMPAIGN.LIST.BUTTONS.EDIT')}
                  </woot-button>
                )}
                { row.campaign_status !== CAMPAIGN_STATUSES.COMPLETED &&
                  row.campaign_status !== CAMPAIGN_STATUSES.CANCELLED && (
                  <woot-button
                    variant="link"
                    icon="dismiss-circle"
                    size="small"
                    color-scheme="secondary"
                    onClick={() => this.cancelCampaign(row)}
                  >
                    {this.$t('CAMPAIGN.LIST.BUTTONS.CANCEL')}
                  </woot-button>
                )}
              </div>
            );
          },
        },
      ];
    },
  },
  watch: {
    sortOrder() {
      this.setSortConfig();
    },
    sortParam() {
      this.setSortConfig();
    },
  },
  mounted() {
    this.setSortConfig();
  },
  methods: {
    editCampaign(campaign) {
      this.$emit('edit', campaign);
    },
    cancelCampaign(campaign) {
      this.$emit('cancel', campaign);
    },
    setSortConfig() {
      this.sortConfig = { [this.sortParam]: this.sortOrder };
    },
  },
};
</script>

<style lang="scss" scoped>
.campaigns-table-wrap::v-deep {
  .ve-table {
    @apply pb-8;
  }

  .ve-table-header-th {
    padding: var(--space-small) var(--space-two) !important;
  }

  .ve-table-body-td {
    padding: var(--space-small) var(--space-two) !important;
  }

  .ve-table-header-th {
    font-size: var(--font-size-mini) !important;
  }
  .ve-table-sort {
    @apply -top-1;
  }
}
</style>
